import { FilterData } from '@nexelem/filters';
import { generateKebabCase } from '@nexelem/utils';
import { isEqual } from 'lodash';
import qs, { IParseOptions } from 'qs';
import { v4 as uuidv4 } from 'uuid';
import { createStore } from 'zustand';
import { createJSONStorage, persist, type StateStorage } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { CustomGridColDef } from '../services';
import { Order } from '@nexelem/api';

export type GridSettings<F = unknown, S = unknown> = {
  id?: string;
  name: string;
  description: string;
  isDefault: boolean;
  columns?: S;
  filters?: F;
};

export type GridStoreState<F = unknown, S = unknown> = {
  currentColumnsSettings: S | null;
  currentOrdering?: Order;
  settings: Array<GridSettings<F, S>>;
  activeId?: string;
  setActiveId: (id?: string) => void;
  saveColumns: (data: S) => void;
  setOrdering: (ordering?: Order) => void;
  addSettings: (data: GridSettings) => void;
  saveSettings: (data: GridSettings[]) => void;
};
export type StoreLoaderData<F = FilterData, S = SavedColDef[]> = { name: string; state?: InitGridStoreState<F, S> };
export type StoreLoaderState = { store: StoreLoaderData };
export type InitGridStoreState<F = FilterData, S = SavedColDef[]> = Pick<
  GridStoreState<F, S>,
  'settings' | 'currentColumnsSettings'
>;
export type GridAPIStore = StateStorage;

const getActiveView = <F, S>(settings: Array<GridSettings<F, S>>) => {
  const options: IParseOptions = {
    ignoreQueryPrefix: true,
    plainObjects: true,
    interpretNumericEntities: true,
    decoder: (str, defaultDecoder, charset, type) => {
      const decoded = defaultDecoder(str, defaultDecoder, charset);
      if (type === 'value') {
        if (!isNaN(Number(decoded))) {
          return Number(decoded);
        }
      }
      return decoded;
    },
  };
  const filters = qs.parse(window.location.search, options)?.data ?? {};
  const activeView = settings.find((_) => isEqual(_.filters, filters));
  return activeView || settings.find((_) => _.isDefault);
};

const createSaveToServerFunction = <F, S>(
  store: GridAPIStore | undefined,
  storeName: string | undefined,
  get: () => GridStoreState<F, S>,
) => {
  if (!store) {
    return () => null;
  }

  return () => {
    const state = get();
    const stateToSave = {
      settings: state.settings,
      currentColumnsSettings: state.currentColumnsSettings,
      // TODO: backward compatibility - remove after demo
      gridSettings: state.settings,
    };
    store.setItem(generateKebabCase('grid-store', storeName), JSON.stringify(stateToSave));
  };
};

/**
 * Migration helper: Remove headerName from stored column definitions
 * This ensures translations work correctly by forcing headerName to come from grid definitions
 */
const migrateColumnsSettings = <S>(settings: S | null): S | null => {
  if (!settings || !Array.isArray(settings)) {
    return settings;
  }

  return settings.map((col: SavedColDef) => {
    // Remove headerName if it exists (legacy data)
    const { field, width, minWidth, isHidden, pinned } = col;
    return { field, width, minWidth, isHidden, pinned };
  }) as S;
};

/**
 * Migration helper: Remove headerName from all settings views
 */
const migrateSettings = <F, S>(settings: Array<GridSettings<F, S>>): Array<GridSettings<F, S>> =>
  settings.map((setting) => {
    const migratedColumns = migrateColumnsSettings(setting.columns);
    return {
      ...setting,
      columns: migratedColumns === null ? undefined : migratedColumns,
    };
  });

export const getGridStore = <F, S>(storeName?: string, store?: GridAPIStore, initState?: InitGridStoreState<F, S>) => {
  const defaultView = initState ? getActiveView<F, S>(initState.settings || []) : undefined;

  if (store) {
    // For async storage: NO persist middleware, manual data loading
    return createStore(
      immer<GridStoreState<F, S>>((set, get) => {
        const saveToServer = createSaveToServerFunction(store, storeName, get);

        return {
          activeId: defaultView?.id || undefined,
          currentColumnsSettings: migrateColumnsSettings(
            initState?.currentColumnsSettings || defaultView?.columns || null,
          ),
          currentOrdering: undefined,
          settings: migrateSettings(initState?.settings || []),
          saveColumns: (currentColumnsSettings: S) => {
            set(() => {
              return { currentColumnsSettings };
            });

            // Manual save to server for async storage
            saveToServer();
          },
          setOrdering: (ordering) => {
            set(() => {
              return { currentOrdering: ordering };
            });
          },
          addSettings: (data) => {
            const result = set(() => {
              const newSettings = {
                ...data,
                id: data.id ?? uuidv4(),
              };
              if (newSettings.isDefault) {
                return {
                  activeId: newSettings.id,
                  settings: [
                    // TODO eslint rule
                    // eslint-disable-next-line arrow-body-style
                    ...get()
                      .settings.map((_) => {
                        return { ..._, isDefault: false };
                      })
                      .filter((_) => _.id !== newSettings.id),
                    newSettings,
                  ],
                };
              }
              return {
                activeId: newSettings.id,
                settings: [...get().settings.filter((_) => _.id !== newSettings.id), newSettings],
              };
            });

            // Manual save to server for async storage
            saveToServer();

            return result;
          },
          saveSettings: (settings) => {
            set(() => {
              return { settings };
            });

            // Manual save to server for async storage
            saveToServer();
          },
          setActiveId: (activeId) => {
            set(() => {
              return { activeId };
            });

            // Manual save to server for async storage
            saveToServer();
          },
        };
      }),
    );
  }

  // For localStorage: use persist middleware
  return createStore(
    persist(
      immer<GridStoreState<F, S>>((set, get) => {
        return {
          activeId: defaultView?.id || undefined,
          currentColumnsSettings: migrateColumnsSettings(
            initState?.currentColumnsSettings || defaultView?.columns || null,
          ),
          currentOrdering: undefined,
          settings: migrateSettings(initState?.settings || []),
          saveColumns: (currentColumnsSettings: S) => {
            set(() => {
              return { currentColumnsSettings };
            });
          },
          setOrdering: (ordering?: { column: string; asc: boolean }) => {
            set(() => {
              return { currentOrdering: ordering };
            });
          },
          addSettings: (data) =>
            set(() => {
              const newSettings = {
                ...data,
                id: data.id ?? uuidv4(),
              };
              if (newSettings.isDefault) {
                return {
                  activeId: newSettings.id,
                  settings: [
                    // TODO eslint rule
                    // eslint-disable-next-line arrow-body-style
                    ...get()
                      .settings.map((_) => {
                        return { ..._, isDefault: false };
                      })
                      .filter((_) => _.id !== newSettings.id),
                    newSettings,
                  ],
                };
              }
              return {
                activeId: newSettings.id,
                settings: [...get().settings.filter((_) => _.id !== newSettings.id), newSettings],
              };
            }),
          saveSettings: (settings) =>
            set(() => {
              return { settings };
            }),
          setActiveId: (activeId) =>
            set(() => {
              return { activeId };
            }),
        };
      }),
      {
        name: generateKebabCase('grid-store', storeName),
        partialize: (state) => {
          // For localStorage: save all changes locally
          return {
            settings: state.settings,
            currentColumnsSettings: state.currentColumnsSettings,
          };
        },
        merge: (persistedState, currentState) => {
          const state = persistedState as GridStoreState<F, S>;
          if (state) {
            const migratedSettings = migrateSettings(state.settings);
            const activeView = getActiveView(migratedSettings);
            return {
              ...currentState,
              ...state,
              settings: migratedSettings,
              activeId: activeView?.id,
              currentColumnsSettings: migrateColumnsSettings(activeView?.columns || state.currentColumnsSettings),
            };
          }
          return currentState;
        },
        storage: createJSONStorage(() => localStorage),
      },
    ),
  );
};

export type SavedColDef = Pick<CustomGridColDef, 'field' | 'width' | 'minWidth' | 'isHidden' | 'pinned'> & {
  field: string;
};

export const createGridStore = <F = FilterData, S = SavedColDef[]>(
  storeName: string,
  store?: GridAPIStore,
  initState?: InitGridStoreState<F, S>,
) => getGridStore<F, S>(storeName, store, initState);
export type GridSettingsStore<F = FilterData, S = SavedColDef[]> = ReturnType<typeof getGridStore<F, S>>;
